"use client";

import { useEffect, useRef, useState } from "react";
import Lottie, { type LottieRefCurrentProps } from "lottie-react";
import type { Locale } from "@/lang";

const cache = new Map<string, Promise<object>>();

/** Playback speed of controlled (scroll-driven) scenes. */
const CONTROLLED_SPEED = 1.6;

/**
 * Scenes that have a phone cut, with the stage ratio of its canvas: `mobile()` in
 * tools/lottie/scenes/<name>.mjs, built as `/lottie/<name>.m.<locale>.json` on a 480-px wide
 * canvas laid out for phone stages (one column, bigger type). Phones get it below Tailwind's
 * `sm`; every other width keeps the 800 × 640 file. The ratio classes are literal so Tailwind
 * generates them; keep them in step with each scene's canvas (480 × 560 is 6:7).
 */
const PHONE_CUTS = new Map<string, string>([
  ["scatter", "max-sm:aspect-[6/7]"],
  ["tasks", "max-sm:aspect-[6/7]"],
  ["notes", "max-sm:aspect-[6/7]"],
  ["planday", "max-sm:aspect-[6/7]"],
  ["agenda", "max-sm:aspect-[6/7]"],
  ["devices", "max-sm:aspect-[6/7]"],
  ["live", "max-sm:aspect-[6/7]"],
  ["privacy", "max-sm:aspect-[6/7]"],
  ["delegate", "max-sm:aspect-[6/7]"],
  ["europe", "max-sm:aspect-square"],
  ["morning", "max-sm:aspect-[5/4]"],
  ["calendar", "max-sm:aspect-[6/7]"],
  ["project", "max-sm:aspect-[6/7]"],
  ["invite", "max-sm:aspect-[6/7]"],
  ["import", "max-sm:aspect-[6/7]"],
  ["resources", "max-sm:aspect-[6/7]"],
]);

/** Tailwind's `max-sm:` query, so the picked file and the reserved stage ratio always agree. */
const PHONE_QUERY = "(width < 40rem)";

/**
 * The stage ratio a scene's phone cut needs below `sm`, to add after the stage's own ratio
 * classes: the box is reserved before the file arrives, so nothing jumps.
 *
 * @param name - Scene name.
 * @returns A `max-sm:aspect-[…]` class when the scene has a phone cut, else an empty string.
 */
export function phoneStageClass(name: string): string {
  return PHONE_CUTS.get(name) ?? "";
}

/**
 * Whether the stage is on a phone-width viewport (below `sm`), live: crossing the
 * breakpoint (a phone turned to landscape) swaps the cut. Null until mounted, so nothing is
 * downloaded before the right cut is known.
 */
function usePhoneViewport(): boolean | null {
  const [phone, setPhone] = useState<boolean | null>(null);
  useEffect(() => {
    const mq = window.matchMedia(PHONE_QUERY);
    const sync = () => setPhone(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return phone;
}

/**
 * Fetches a generated landing animation once per session and shares the promise, so the
 * desktop stage and a later remount never download the same file twice.
 *
 * @param name - Scene name (`tools/lottie/scenes/<name>.mjs`).
 * @param locale - Language the scene's text was baked in.
 * @param localized - Whether the scene has one file per language.
 * @returns The Lottie JSON.
 */
export function loadStoryAnimation(name: string, locale: Locale, localized = true): Promise<object> {
  const url = localized ? `/lottie/${name}.${locale}.json` : `/lottie/${name}.json`;
  let p = cache.get(url);
  if (!p) {
    p = fetch(url).then((r) => {
      if (!r.ok) throw new Error(`${url}: ${r.status}`);
      return r.json() as Promise<object>;
    });
    // A failed fetch must not poison the cache: the next activation retries.
    p.catch(() => cache.delete(url));
    cache.set(url, p);
  }
  return p;
}

/**
 * The scene's loopable idle segment, from its `idle` marker (see `idle()` in
 * tools/lottie/lib/kit.mjs), or null when the scene simply holds its last frame.
 *
 * @param data - Lottie JSON.
 * @returns `[start, end]` frames, or null.
 */
function idleSegment(data: object): [number, number] | null {
  const markers = (data as { markers?: { cm?: string; tm: number; dr: number }[] }).markers;
  const m = markers?.find((x) => x.cm === "idle");
  return m ? [m.tm, m.tm + m.dr] : null;
}

/** Whether the visitor asked the OS for reduced motion (live). */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return reduced;
}

type StoryLottieProps = {
  /** Scene name. */
  name: string;
  locale: Locale;
  /** Start downloading (the step is near the viewport). */
  load: boolean;
  /** Play from the first frame; the last frame is held afterwards. */
  active: boolean;
  /** Accessible description of what the animation shows. */
  label: string;
  localized?: boolean;
  className?: string;
  /**
   * Controlled mode for scenes split into acts: the frame to reach. Moving forward plays
   * up to it in real time; moving back jumps. `active` is ignored when set.
   */
  target?: number;
  /**
   * Replay counter: each new value plays the scene again from its first frame, even after
   * it completed (a click on the stage). Ignored with reduced motion and in controlled mode.
   */
  replay?: number;
};

/**
 * A generated product animation that plays when it becomes active and then holds its
 * last frame (the "result" picture). Played to the end once, it keeps that picture for
 * the rest of the visit; interrupted, it rewinds so the next activation starts over.
 * With reduced motion it shows the last frame only.
 *
 * Frame 0 of every scene is a complete picture, so the player is meaningful before and
 * after playing; until the JSON arrives an empty, fixed-ratio box keeps the layout
 * stable. Below `sm`, scenes listed in `PHONE_CUTS` load their phone cut instead (give
 * the box `phoneStageClass(name)` so its ratio matches).
 */
export function StoryLottie({
  name,
  locale,
  load,
  active,
  label,
  localized = true,
  className,
  target,
  replay = 0,
}: StoryLottieProps) {
  // The loaded file, keyed by cut and language: a new key remounts the player.
  const [anim, setAnim] = useState<{ key: string; data: object } | null>(null);
  // Key of the file whose DOM is ready (a swapped cut is not ready until it has loaded).
  const [readyKey, setReadyKey] = useState<string | null>(null);
  const data = anim?.data ?? null;
  const ready = anim !== null && readyKey === anim.key;
  const ref = useRef<LottieRefCurrentProps | null>(null);
  // Once a scene has played to its end it keeps its result picture: scrolling back up
  // must not replay the same demo every time (it reads as a glitch, not as motion).
  const completedRef = useRef(false);
  const reduced = usePrefersReducedMotion();
  const phone = usePhoneViewport();
  const file = phone === null ? null : phone && PHONE_CUTS.has(name) ?`${name}.m` : name;

  useEffect(() => {
    if (!load || !file) return;
    let alive = true;
    const key = `${file}.${locale}`;
    loadStoryAnimation(file, locale, localized)
      .then((d) => alive && setAnim((prev) => (prev?.key === key ? prev : { key, data: d })))
      .catch(() => {
        // Network hiccup: the empty stage stays, the copy next to it carries the message.
      });
    return () => {
      alive = false;
    };
  }, [load, file, locale, localized]);

  // Controlled mode: play forward to the target frame (a bit faster than real time, so the
  // picture keeps up with the scroll), jump when going back. lottie-web reports
  // `currentFrame` relative to the segment being played, so the absolute frame adds
  // `firstFrame`; a jump first drops the segment, or it would land relative to it.
  useEffect(() => {
    const anim = ref.current;
    const item = anim?.animationItem;
    if (!anim || !item || !ready || target === undefined) return;
    const current = Math.round(item.firstFrame + item.currentFrame);
    if (reduced || target <= current) {
      item.resetSegments(true);
      anim.goToAndStop(target, true);
    } else {
      anim.setSpeed(CONTROLLED_SPEED);
      anim.playSegments([current, target], true);
    }
  }, [target, ready, reduced]);

  const idle = data ? idleSegment(data) : null;
  const activeRef = useRef(active);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  /** Plays the main action from the first frame (up to the idle loop when the scene has one). */
  const playMain = (anim: LottieRefCurrentProps) => {
    const item = anim.animationItem;
    if (item) item.loop = false;
    if (idle) anim.playSegments([0, idle[0]], true);
    else anim.goToAndPlay(0, true);
  };

  /** Loops the scene's idle segment (the gentle after-life of a finished scene). */
  const playIdle = (anim: LottieRefCurrentProps) => {
    const item = anim.animationItem;
    if (!idle || !item) return;
    item.loop = true;
    anim.playSegments(idle, true);
  };

  useEffect(() => {
    const anim = ref.current;
    if (!anim || !ready || target !== undefined) return;
    const last = Math.max(0, (anim.getDuration(true) ?? 1) - 1);
    if (reduced) {
      anim.goToAndStop(idle ? idle[0] : last, true);
      return;
    }
    if (completedRef.current) {
      // Finished scenes keep their result: idling while on screen, paused off screen.
      if (!idle) anim.goToAndStop(last, true);
      else if (active) playIdle(anim);
      else anim.pause();
      return;
    }
    if (active) {
      playMain(anim);
      return;
    }
    // Interrupted mid-way: rewind, but only once the stage has faded this scene out
    // (rewinding during the fade would flash frame 0 over the outgoing picture).
    const id = window.setTimeout(() => anim.goToAndStop(0, true), 500);
    return () => window.clearTimeout(id);
    // playMain/playIdle only close over `idle`, which follows `data` like `ready` does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, ready, reduced, target]);

  // Replay on demand (0 is the initial value: nothing asked yet).
  useEffect(() => {
    const anim = ref.current;
    if (!anim || !ready || !replay || reduced || target !== undefined) return;
    playMain(anim);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replay, ready, reduced, target]);

  return (
    <div className={className} role="img" aria-label={label}>
      {anim && (
        <Lottie
          key={anim.key}
          lottieRef={ref}
          animationData={anim.data}
          loop={false}
          autoplay={false}
          renderer="svg"
          rendererSettings={{ preserveAspectRatio: "xMidYMid meet", progressiveLoad: false }}
          onDOMLoaded={() => setReadyKey(anim.key)}
          onComplete={() => {
            completedRef.current = true;
            // The main action just ended: slide straight into the idle loop if still on screen.
            const anim = ref.current;
            if (anim && idle && !reduced && activeRef.current && target === undefined) playIdle(anim);
          }}
          style={{ width: "100%", height: "100%" }}
        />
      )}
    </div>
  );
}
